'use client'

// The last boundary: a crash in the ROOT LAYOUT itself, which no error.tsx can
// hold. It replaces the whole document, so the ground, the fonts, the theme and
// every stylesheet are gone and this file has to bring its own. That is why it is
// self-contained: its own <html> and <body>, its own <title>, an inline <style>
// carrying the tokens (cream by day, slate when the system is dark), and the
// netti mark drawn inline. Nothing it shows depends on a file that might not load.
//
// It speaks the same words as NettiError and retries the same way as error.tsx:
// quietly at 1.5s / 4s / 10s while the tab is visible, capped per digest, with
// unstable_retry (which re-fetches the route, unlike reset). A failed code
// download is a stale deploy, so it reloads once instead. Everything is still
// reported, to Sentry (a no-op without a DSN) and PostHog.

import * as Sentry from '@sentry/nextjs'
import posthog from 'posthog-js'
import { useEffect, useState } from 'react'
import OrganismSpinner from '../components/brand/OrganismSpinner'
import { useAutoRetry } from '../components/brand/useAutoRetry'
import { chunkVerdict, isChunkError } from '../lib/os/resilience'

const CSS = `
:root{--bg:#fffaf1;--ink:hsl(217 42% 19%);--ink-2:hsl(217 42% 19%/.64);--ink-3:hsl(217 42% 19%/.42);--glass:hsl(217 42% 19%/.07);--line:hsl(217 42% 19%/.15);color-scheme:light}
@media (prefers-color-scheme:dark){:root{--bg:#444e5c;--ink:hsl(217 24% 90%);--ink-2:hsl(217 24% 90%/.72);--ink-3:hsl(217 24% 90%/.5);--glass:hsl(217 24% 90%/.1);--line:hsl(217 24% 90%/.2);color-scheme:dark}}
*{box-sizing:border-box}
html,body{margin:0;background:var(--bg);color:var(--ink)}
body{min-height:100svh;display:grid;place-items:center;padding:clamp(4.5rem,14vh,7rem) clamp(1.25rem,3.5vw,2.6rem) clamp(3rem,10vh,5rem);font-family:system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif;font-weight:400;-webkit-font-smoothing:antialiased}
.ge{display:flex;flex-direction:column;align-items:center;gap:clamp(1rem,2.4vh,1.6rem);max-width:34rem;text-align:center}
.ge>*{opacity:0;animation:ge-in .5s cubic-bezier(.22,1,.36,1) both;animation-delay:calc(var(--hold,.18s) + var(--i,0)*70ms)}
.ge>:nth-child(2){--i:1}.ge>:nth-child(3){--i:2}
.ge[data-state=reconnecting]{--hold:.45s}
.ge-mark{font-size:clamp(3.2rem,9vw,5.2rem);line-height:1;color:var(--ink-2)}
.ge-mark svg{width:1em;height:1em;display:block}
.io-spin__petals{transform-box:view-box;transform-origin:199.05px 198.6px;animation:ge-spin 21s linear infinite}
.ge[data-state=reconnecting] .io-spin__petals{animation-duration:7s}
.ge-line{margin:0;font-size:clamp(1.3rem,.72rem + 1.8vw,2.44rem);font-weight:400;line-height:1.08;letter-spacing:-.05em;text-wrap:balance}
.ge-body{margin:.5rem 0 0;font-size:clamp(.95rem,.92rem + .18vw,1.05rem);line-height:1.5;color:var(--ink-2);text-wrap:pretty}
.ge-pill{display:inline-flex;align-items:center;gap:.72rem;padding:2px 2px 2px 1.35rem;border-radius:999px;border:1px solid var(--line);background:var(--glass);color:var(--ink);font:inherit;font-size:1.0625rem;letter-spacing:-.01em;cursor:pointer;-webkit-tap-highlight-color:transparent}
.ge-pill:active{transform:scale(.98)}
.ge-pill:disabled{opacity:.6;cursor:default}
.ge-disc{width:44px;height:44px;border-radius:50%;background:#fff;color:hsl(217 46% 17%);display:grid;place-items:center}
.ge-disc svg{width:18px;height:18px}
.ge-link{background:none;border:0;padding:.35rem 0;font:inherit;font-size:.85rem;color:var(--ink-3);text-decoration:underline;text-underline-offset:4px;cursor:pointer}
.ge-link:hover{color:var(--ink)}
@keyframes ge-in{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}
@keyframes ge-spin{to{transform:rotate(360deg)}}
@media (prefers-reduced-motion:reduce){.ge>*{animation:none;opacity:1}.io-spin__petals{animation:none!important}}
`

const COPY = {
  reconnecting: { line: 'one moment, we’re reconnecting.', body: '' },
  failed: { line: 'that didn’t load.', body: 'nothing in your memory changed. please try again.' },
  updated: { line: 'innernet just updated. one moment.', body: '' },
} as const

export default function GlobalError({
  error,
  unstable_retry,
}: {
  error: Error & { digest?: string }
  unstable_retry: () => void
}) {
  const chunk = isChunkError(error)
  const [reloaded, setReloaded] = useState<'updating' | 'gave-up' | null>(null)

  useEffect(() => {
    Sentry.captureException(error)
    // Sentry has no DSN here, so this was a no-op and root crashes went unseen;
    // PostHog error tracking is where every other client error lands
    try { posthog.captureException(error, { where: 'global', digest: error.digest, chunk }) } catch { /* never throw on an error */ }
  }, [error, chunk])

  // a stale deploy: the layout itself is gone, and so is ChunkRecovery with it.
  // Reload once; a second failure inside the minute stops and says so.
  useEffect(() => {
    if (!chunk) return
    let store: Storage | null = null
    try { store = window.sessionStorage } catch { /* blocked: chunkVerdict gives up */ }
    if (chunkVerdict(store, Date.now()) === 'reload') {
      setReloaded('updating')
      const t = setTimeout(() => window.location.reload(), 700)
      return () => clearTimeout(t)
    }
    setReloaded('gave-up')
  }, [chunk])

  const { phase, busy, retryNow } = useAutoRetry({ where: 'global', error, retry: unstable_retry, enabled: !chunk })
  const state = chunk
    ? reloaded === 'gave-up' ? 'failed' : 'updated'
    : phase === 'waiting' ? 'reconnecting' : 'failed'
  const c = COPY[state]
  const door = state === 'failed'
  const press = chunk ? () => window.location.reload() : retryNow

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>innernet</title>
        <style dangerouslySetInnerHTML={{ __html: CSS }} />
      </head>
      <body>
        <main className="ge" data-state={state} role={door ? 'alert' : 'status'} aria-live={door ? 'assertive' : 'polite'}>
          <span className="ge-mark">
            <OrganismSpinner size="1em" tone="current" />
          </span>
          <div>
            <h1 className="ge-line">{c.line}</h1>
            {c.body ? <p className="ge-body">{c.body}</p> : null}
          </div>
          {door ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.9rem' }}>
              <button type="button" className="ge-pill" onClick={press} disabled={busy}>
                <span>{chunk ? 'reload' : 'try again'}</span>
                <span className="ge-disc" aria-hidden>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5l7 7-7 7" /></svg>
                </span>
              </button>
              <a className="ge-link" href="/">go home</a>
            </div>
          ) : null}
        </main>
      </body>
    </html>
  )
}
